import { useNavigate, useRouter } from "@tanstack/react-router";
import { RefreshCw, Lock, AlertTriangle, LogOut } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { Combobox } from "@/components/Combobox";
import { type ComboboxOption } from "@/components/Combobox/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useSubscriptionPlanFeatures, SUBSCRIPTION_FEATURES } from "@/hooks/use-subscription-plan-features";

export function PaywallOverlay() {
  const { hasFeatureAccess, error } = useSubscriptionPlanFeatures();
  const hasSupplyChainAccess = hasFeatureAccess(SUBSCRIPTION_FEATURES.SUPPLY_CHAINS_APP_ACCESS);
  const { activeBusinessUnitId, setActiveBusinessUnitId, businessUnits, loading } = useActiveBusinessUnit();
  const auth = useAuth();
  const router = useRouter();
  const navigate = useNavigate();

  const hasMultipleBusinessUnits = businessUnits && businessUnits.length > 1;

  // Business unit picker options for the modal context
  const businessUnitOptions: ComboboxOption[] = businessUnits
    ? businessUnits.map((bu) => ({ value: bu.id, label: bu.name })).sort((a, b) => a.label.localeCompare(b.label))
    : [];

  const handleLogout = async () => {
    try {
      await auth.logout();
      await router.invalidate();
      return navigate({ to: "/login" });
    } catch (error) {
      console.error("Error during logout:", error);
      return navigate({ to: "/login" });
    }
  };

  if (hasSupplyChainAccess || loading) {
    return null;
  }

  return (
    <AlertDialog open={true}>
      <AlertDialogContent className="sm:max-w-lg">
        {error ? (
          <>
            <AlertDialogHeader>
              <div className="mx-auto w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <AlertDialogTitle className="text-center text-2xl">Failed to Check Subscription Status</AlertDialogTitle>
              <AlertDialogDescription className="text-center pt-4">
                We couldn't verify your subscription status due to a network error.
              </AlertDialogDescription>
              <p className="text-center text-sm text-muted-foreground mt-2">
                Please check your connection and try refreshing the page.
              </p>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction asChild>
                <Button variant="outline" onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                <Lock className="w-8 h-8 text-blue-600" />
              </div>
              <AlertDialogTitle className="text-center text-2xl">Upgrade Required</AlertDialogTitle>
              <AlertDialogDescription className="text-center pt-4">
                Your current subscription plan doesn't include access to the Supply Chains application.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {/* Business Unit Switcher */}
            {hasMultipleBusinessUnits && (
              <Card className="p-5 bg-muted/50 border-border mt-6">
                <div className="text-left space-y-4">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-blue-600" />
                    <label className="block text-sm font-semibold text-gray-800">Switch Business Unit</label>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Try switching to a different business unit to check if you have access through another subscription.
                  </p>
                  <Combobox
                    type="single"
                    className="bg-white border border-input h-11 w-full text-gray-900"
                    options={businessUnitOptions}
                    loading={loading}
                    selected={activeBusinessUnitId || null}
                    onChange={setActiveBusinessUnitId}
                    isModal
                  />
                </div>
              </Card>
            )}

            <p className="text-center text-sm text-muted-foreground">
              To upgrade your plan and gain access to this feature, please contact Breedr.
            </p>

            <AlertDialogFooter>
              <AlertDialogAction asChild>
                <Button variant="outline" onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
